import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronRight, FiSearch } from "react-icons/fi";
import "../../common/GoogleAddressAutocompleteInput/GoogleAddressAutocompleteInput.css";

let autocompleteInstance;

const loadGoogleScript = (callback) => {
  if (window.google && window.google.maps && window.google.maps.places) {
    callback();
    return;
  }
  const existing = document.querySelector(
    `script[src*="maps.googleapis.com/maps/api/js"]`
  );
  if (existing) {
    existing.addEventListener("load", callback);
    return;
  }
  const script = document.createElement("script");
  script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_PLACES_API_KEY}&libraries=places`;
  script.async = true;
  script.defer = true;
  script.onload = callback;
  document.head.appendChild(script);
};

const SearchBar = () => {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Tag pac-container with google-address-pac so existing CSS applies
  useEffect(() => {
    const observer = new MutationObserver(() => {
      document.querySelectorAll(".pac-container:not(.google-address-pac)").forEach((el) => {
        el.classList.add("google-address-pac");
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    loadGoogleScript(() => {
      if (!inputRef.current) return;
      autocompleteInstance = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ["geocode"],
          componentRestrictions: { country: "ng" },
        }
      );
      autocompleteInstance.setFields(["formatted_address", "geometry"]);
      autocompleteInstance.addListener("place_changed", () => {
        const place = autocompleteInstance.getPlace();
        if (place?.formatted_address) {
          setQuery(place.formatted_address);
          navigate(
            `/new-homes/results?search=${encodeURIComponent(place.formatted_address)}`
          );
        }
      });
    });
    return () => {
      if (autocompleteInstance) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteInstance);
      }
    };
  }, []);

  const handleSearch = () => {
    if (query.trim()) {
      navigate(`/new-homes/results?search=${encodeURIComponent(query)}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <div className="flex items-center w-full max-w-4xl bg-white rounded-full shadow-lg overflow-hidden">
      <label htmlFor="new-apartments-search" className="sr-only">
        Search by region, suburb or postcode
      </label>
      <span className="pl-5 text-gray-400 flex-shrink-0">
        <FiSearch size={18} aria-hidden="true" />
      </span>
      <input
        id="new-apartments-search"
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search by region, suburb or postcode"
        className="flex-1 px-2 py-2.5 text-sm text-gray-600 placeholder-gray-400 outline-none bg-transparent"
        aria-label="Search by region, suburb or postcode"
        autoComplete="off"
      />
      <button
        onClick={handleSearch}
        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2.5 text-sm rounded-full m-1 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 flex items-center gap-1 flex-shrink-0"
        aria-label="Search"
      >
        Search <FiChevronRight size={18} aria-hidden="true" className="w-5 h-5 -ml-1" />
      </button>
    </div>
  );
};

export default SearchBar;
